import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router'
import { Observable, of, forkJoin, from, firstValueFrom } from 'rxjs'
import { map, catchError, take, switchMap } from 'rxjs/operators'
import {
  EXP_SCHEDULE_LIST_VIEW_ID,
  EXP_SCHEDULE_LIST_VIEW_VARIATION,
  EXP_SCHEDULE_MAP_VIEW_ID,
  EXP_SCHEDULE_MAP_VIEW_VARIATION,
  EXP_GLOBAL_SEARCH_ID,
  EXP_GLOBAL_SEARCH_VARIATION,
  EXP_NEXTGEN_ATHLETE_PROFILE_ID,
  EXP_NEXTGEN_ATHLETE_PROFILE_VARIATION
} from '../experimentation/experiment.model'
import { ExperimentationService } from '../experimentation/experimentation.service'
import { PlatformService } from '../../singleton-services/platform.service'
import { VALIDATION_RULES_FOR_ID_PARAMS } from '../utility-functions/regex.utility'

/**
 * NextGenUniversalGuard - Dynamic NextGen Routing Guard
 *
 * This guard handles dynamic routing to NextGen components based on active experiments.
 * It checks if users are enrolled in specific experiments and redirects them to the
 * corresponding NextGen routes while preserving query parameters.
 *
 * ## Adding a New Experiment
 *
 * To add a new experiment that redirects users to NextGen routes:
 *
 * 1. **Define and import experiment constants** - Add to `experiment.model.ts` and import here:
 *    ```typescript
 *    export const EXP_NEXTGEN_EXAMPLE_ID = 'cxp_12345_nextgen_example'
 *    export const EXP_NEXTGEN_EXAMPLE_VARIATION = '1'
 *    ```
 *
 * 2. **Add a new RouteConfig** to the `routeConfigs` array:
 *    ```typescript
 *    {
 *      experiments: [
 *        { id: EXP_NEXTGEN_EXAMPLE_ID, variation: EXP_NEXTGEN_EXAMPLE_VARIATION }
 *      ],
 *      matcher: (url: string) => {
 *        const baseUrl = url.split('?')[0]
 *        return baseUrl === '/example' || baseUrl === '/example/'
 *      },
 *      transformer: (url: string) => {
 *        const queryParams = url.includes('?') ? url.substring(url.indexOf('?')) : ''
 *        return `/nextgen/example${queryParams}`
 *      }
 *    }
 *    ```
 *
 * 3. **Add the guard to route modules** where needed:
 *    ```typescript
 *    // In example-routing.module.ts
 *    import { NextGenUniversalGuard } from '../shared/guards/next-gen-universal.guard'
 *
 *    canActivate: [NextGenUniversalGuard, MetaGuard]
 *    ```
 *
 * ## Multiple Experiments for Same Route
 *
 * You can configure multiple experiments to redirect to the same NextGen route:
 * ```typescript
 * {
 *   experiments: [
 *     { id: EXP_FEATURE_A_ID, variation: EXP_FEATURE_A_VARIATION },
 *     { id: EXP_FEATURE_B_ID, variation: EXP_FEATURE_B_VARIATION }
 *   ],
 *   matcher: (url: string) => url.startsWith('/dashboard'),
 *   transformer: (url: string) => url.replace('/dashboard', '/nextgen/dashboard')
 * }
 * ```
 *
 * ## How It Works
 *
 * - **Browser-only**: Skips redirection during SSR to prevent hydration issues
 * - **Experiment checking**: Uses forkJoin to check multiple experiments in parallel
 * - **Query preservation**: Maintains query parameters in redirected URLs
 * - **Error handling**: Gracefully handles experiment service errors by allowing navigation
 */

interface RouteConfig {
  experiments: Array<{
    id: string
    variation: string
  }>
  matcher: (url: string) => boolean
  transformer?: (url: string) => string
  asyncTransformer?: (url: string) => Promise<string>
}

@Injectable({
  providedIn: 'root'
})
export class NextGenUniversalGuard implements CanActivate {
  private readonly routeConfigs: RouteConfig[] = [
    {
      experiments: [
        { id: EXP_SCHEDULE_LIST_VIEW_ID, variation: EXP_SCHEDULE_LIST_VIEW_VARIATION },
        { id: EXP_SCHEDULE_MAP_VIEW_ID, variation: EXP_SCHEDULE_MAP_VIEW_VARIATION }
      ],
      matcher: (url: string) => {
        const baseUrl = url.split('?')[0]
        return baseUrl === '/events' || baseUrl === '/events/'
      },
      transformer: (url: string) => {
        const queryParams = url.includes('?') ? url.substring(url.indexOf('?')) : ''
        return `/nextgen/events${queryParams}`
      }
    },
    {
      experiments: [{ id: EXP_GLOBAL_SEARCH_ID, variation: EXP_GLOBAL_SEARCH_VARIATION }],
      matcher: (url: string) => {
        const baseUrl = url.split('?')[0]
        return baseUrl === '/search' || baseUrl === '/search/'
      },
      transformer: (url: string) => {
        const queryParams = url.includes('?') ? url.substring(url.indexOf('?')) : ''
        return `/nextgen/search${queryParams}`
      }
    },
    {
      experiments: [{ id: EXP_NEXTGEN_ATHLETE_PROFILE_ID, variation: EXP_NEXTGEN_ATHLETE_PROFILE_VARIATION }],
      matcher: (url: string) => {
        const baseUrl = url.split('?')[0]
        return VALIDATION_RULES_FOR_ID_PARAMS.ATHLETE_PROFILE_URL.test(baseUrl)
      },
      transformer: (url: string) => {
        const coreId = this.extractCoreIdFromUrl(url)
        const queryParams = url.includes('?') ? url.substring(url.indexOf('?')) : ''
        return `/nextgen/people/${coreId}${queryParams}`
      }
    }
  ]

  constructor(private experimentService: ExperimentationService, private platformService: PlatformService) {}

  private extractCoreIdFromUrl(url: string): string {
    const match = url.match(VALIDATION_RULES_FOR_ID_PARAMS.ATHLETE_PROFILE_URL)
    return match ? match[1] : ''
  }

  canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Early exit for SSR
    if (!this.platformService || !this.platformService.isBrowser) {
      return of(true)
    }

    const currentUrl = state.url

    // Safety check for routeConfigs
    if (!this.routeConfigs || !Array.isArray(this.routeConfigs)) {
      return of(true)
    }

    // Find matching route config
    const matchingConfig = this.routeConfigs.find(config => config.matcher(currentUrl))

    if (!matchingConfig) {
      return of(true) // No matching config, allow navigation
    }

    // Check all experiments for this route
    if (!matchingConfig.experiments || !Array.isArray(matchingConfig.experiments)) {
      return of(true)
    }

    const experimentChecks = matchingConfig.experiments.map(exp => {
      return this.experimentService.isInVariation(exp.id, exp.variation, false).pipe(
        catchError(() => of(false)), // Default to not redirecting on error
        take(1) // Ensure each individual experiment check completes
      )
    })

    // Handle empty experiment checks array (forkJoin with empty array completes immediately)
    if (experimentChecks.length === 0) {
      return of(true)
    }

    return forkJoin(experimentChecks).pipe(
      switchMap(results => {
        // If any experiment is active, redirect to NextGen
        const shouldRedirect = results.some(isActive => isActive)

        if (shouldRedirect) {
          // Handle async transformer
          if (matchingConfig.asyncTransformer) {
            return from(matchingConfig.asyncTransformer(currentUrl)).pipe(
              map(redirectUrl => {
                if (redirectUrl !== currentUrl) {
                  window.location.href = redirectUrl
                  return false
                }
                return true
              }),
              catchError(() => of(true)) // Allow navigation on async transformer error
            )
          }

          // Handle sync transformer
          if (matchingConfig.transformer) {
            const redirectUrl = matchingConfig.transformer(currentUrl)
            window.location.href = redirectUrl
            return of(false)
          }
        }

        return of(true)
      }),
      catchError(() => of(true)), // Allow navigation on error
      take(1)
    )
  }
}
